import { c as _c } from "react/compiler-runtime";
import { useFormatter, useTranslations } from 'next-intl';
import { Progress } from '@/components/ui/app-progress';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferHorizontalIconSolid, LimitationIconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { outcomeHueToVar } from '@/lib/chart/outcome-hue';
import { useFormattedCurrency } from '@/lib/currency';
import type { OutcomeRowVM } from './derive';
import { formatResolvedPct } from './other-outcomes-format';
import { OutcomeThumb } from './other-outcomes-parts';
interface OtherOutcomeCardProps {
  row: OutcomeRowVM;
  rank: number;
  marketPubkey: string;
}
const METRIC_DIVIDER = '•';
const MAX_CONVICTION_PERCENT = 200;
export function OtherOutcomeCard(t0) {
  const $ = _c(76);
  const {
    row,
    rank,
    marketPubkey
  } = t0;
  const t = useTranslations("market.resolvedPanel");
  const formatter = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== row.index || $[1] !== row.label) {
    t1 = {
      title: row.label,
      index: row.index
    };
    $[0] = row.index;
    $[1] = row.label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== marketPubkey) {
    t2 = {
      marketPubkey
    };
    $[3] = marketPubkey;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const colors = useOutcomeColor(t1, t2);
  let t3;
  if ($[5] !== row.finalSharePct) {
    t3 = formatResolvedPct(row.finalSharePct);
    $[5] = row.finalSharePct;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const finalShare = t3;
  const t4 = row.isWinner ? "true" : undefined;
  let t5;
  if ($[7] !== formatter || $[8] !== rank || $[9] !== row.isWinner) {
    t5 = row.isWinner ? undefined : formatter.number(rank);
    $[7] = formatter;
    $[8] = rank;
    $[9] = row.isWinner;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== marketPubkey || $[12] !== row.imageNsfw || $[13] !== row.imageUrl || $[14] !== row.index || $[15] !== row.isWinner || $[16] !== row.label || $[17] !== t5) {
    t6 = <OutcomeThumb marketPubkey={marketPubkey} index={row.index} label={row.label} imageUrl={row.imageUrl} imageNsfw={row.imageNsfw} size="size-12" showSettledBadge={row.isWinner} rankLabel={t5} />;
    $[11] = marketPubkey;
    $[12] = row.imageNsfw;
    $[13] = row.imageUrl;
    $[14] = row.index;
    $[15] = row.isWinner;
    $[16] = row.label;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== row.label) {
    t7 = <span className="break-words text-body font-medium text-foreground-primary">{row.label}</span>;
    $[19] = row.label;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t) {
    t8 = t("tableVolume");
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <span className="sr-only">{t8}</span>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon icon={ArrowDataTransferHorizontalIconSolid} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = row.volumeTotalD6 ?? "0";
  let t12;
  if ($[26] !== formatD6 || $[27] !== t11) {
    t12 = formatD6(t11, "compact");
    $[26] = formatD6;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = <span>{t12}</span>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t9) {
    t14 = <span className="inline-flex items-center gap-1">{t9}{t10}{t13}</span>;
    $[31] = t13;
    $[32] = t9;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span aria-hidden={true} className="mx-0.5 text-foreground-tertiary">{METRIC_DIVIDER}</span>;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("tablePeak");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16) {
    t17 = <span className="sr-only">{t16}</span>;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Icon icon={LimitationIconSolid} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== row.peakConsensusPct) {
    t19 = formatResolvedPct(row.peakConsensusPct);
    $[40] = row.peakConsensusPct;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t19) {
    t20 = <span>{t19}</span>;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t17 || $[45] !== t20) {
    t21 = <span className="inline-flex items-center gap-1">{t17}{t18}{t20}</span>;
    $[44] = t17;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t14 || $[48] !== t21) {
    t22 = <div className="flex flex-wrap items-center gap-1 text-caption tabular-nums text-foreground-secondary">{t14}{t15}{t21}</div>;
    $[47] = t14;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22 || $[51] !== t7) {
    t23 = <div className="flex min-w-0 flex-1 flex-col gap-1.5">{t7}{t22}</div>;
    $[50] = t22;
    $[51] = t7;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t23 || $[54] !== t6) {
    t24 = <div className="flex min-w-0 items-center gap-3">{t6}{t23}</div>;
    $[53] = t23;
    $[54] = t6;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  const t25 = Math.min(MAX_CONVICTION_PERCENT, Math.max(0, row.finalSharePct));
  const t26 = row.label;
  let t27;
  if ($[56] !== t) {
    t27 = t("tableFinal");
    $[56] = t;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  const t28 = `${t26}: ${t27}`;
  let t29;
  if ($[58] !== colors.hue) {
    t29 = outcomeHueToVar(colors.hue, "text");
    $[58] = colors.hue;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t29) {
    t30 = {
      backgroundColor: t29
    };
    $[60] = t29;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== finalShare || $[63] !== t25 || $[64] !== t28 || $[65] !== t30) {
    t31 = <Progress value={t25} max={MAX_CONVICTION_PERCENT} aria-label={t28} aria-valuetext={finalShare} className="h-1.5 min-w-0 flex-1 bg-background-tertiary" indicatorStyle={t30} />;
    $[62] = finalShare;
    $[63] = t25;
    $[64] = t28;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== finalShare) {
    t32 = <span className="shrink-0 text-caption tabular-nums text-foreground-secondary">{finalShare}</span>;
    $[67] = finalShare;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t31 || $[70] !== t32) {
    t33 = <div className="flex min-w-0 items-center gap-3">{t31}{t32}</div>;
    $[69] = t31;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t24 || $[73] !== t33 || $[74] !== t4) {
    t34 = <li data-testid="resolved-panel-outcome-row" data-winner={t4} className="flex min-w-0 flex-col gap-3">{t24}{t33}</li>;
    $[72] = t24;
    $[73] = t33;
    $[74] = t4;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  return t34;
}